/**
 * @lines98/client/lib/swatch-gallery — ПЕРЕИСПОЛЬЗУЕМЫЙ ствол «сетка свотчей одной
 * оси оформления». appearance-skins.md §5.2 (модель «ствол → ветки»).
 *
 * ЗАЧЕМ / WHY THIS EXISTS (owner-directive 2026-05-30 «общее — модуль, не копия»):
 *   Под-экран «Оформление» имеет ДВЕ вкладки [Доска | Шары], каждая — сетка свотчей.
 *   Поведение «сетка свотчей + один свотч (мини-доска + имя + слот бейджа) + selected-
 *   state + выбор» ОДИНАКОВО на обеих осях. Это ОДИН модуль, параметризованный осью
 *   (палитра / материал) — обе вкладки монтируют его с разными данными, НЕ копией
 *   (обобщение прежнего `components/skin-gallery`, AC-9).
 *
 * СВОТЧ = мини-доска (тот же `lib/board-view`, что лобби-карточки) в КОМБИНАЦИИ
 *   «значение этой оси × текущее значение ДРУГОЙ оси» через общий `lib/board-
 *   appearance` (превью отражает реальный вид). Имя свотча — must-visible. Слот
 *   бейджа в DOM есть всегда, но рендерится только при непустом `badge` (AC-5/AC-5.1)
 *   — сейчас пуст → скрыт.
 *
 * SCOPE: построение сетки + свотчей + selected-state + колбэк выбора. NOT IN SCOPE:
 *   данные осей (skin-registry); применение к боевой доске (board-appearance); вкладки
 *   и закреплённое превью (под-экран `components/skin-gallery`); персист (client-prefs
 *   — делает потребитель в `onSelect`).
 *
 * Cross-references: appearance-skins.md §5.2; `lib/board-view`; `lib/board-appearance`;
 * `components/skin-gallery` (под-экран-потребитель); `skin-gallery.css` (вид свотчей).
 */

import { startState } from '@lines98/shared';

import { applyAppearance } from './board-appearance.js';
import { mountBoardView } from './board-view.js';
import { t } from './i18n/index.js';

/** Фиксированный представительный seed для превью (static, никогда не зачётный). */
const PREVIEW_SEED = 0x10b98;

/** Один элемент оси (палитра ИЛИ материал) — общий минимум для свотча. */
export interface SwatchItem {
  /** Стабильный id (возвращается в onSelect; persist решает потребитель). */
  readonly id: string;
  /** Видимое имя (must-visible подпись свотча). */
  readonly name: string;
  /** Future-paid флаг (сейчас не блокирует выбор — §2.3 AC-5). */
  readonly premium?: boolean;
  /** Слот бейджа; рендерится только если непустой (сейчас пусто → скрыт, AC-5). */
  readonly badge?: string;
}

export interface SwatchGalleryOptions {
  /**
   * Что рисует свотч этой оси (owner-directive 2026-06-16: оси НЕ смешиваются):
   *   'board' (ось «Доска») — мини-доска БЕЗ шаров (только фон/клетки палитры);
   *   'balls' (ось «Шары») — 7 шаров в материале, БЕЗ доски.
   */
  readonly mode: 'board' | 'balls';
  /** Элементы оси в порядке отображения. */
  readonly items: readonly SwatchItem[];
  /** Id текущего выбранного элемента ЭТОЙ оси. */
  readonly selectedId: string;
  /** ARIA-имя сетки (role=list). */
  readonly ariaLabel: string;
  /**
   * Превью одного свотча = комбинация осей. `appearanceFor(itemId)` возвращает
   * {paletteId, ballStyleId} для свотча `itemId` (значение этой оси × текущее значение
   * другой оси) — потребитель знает, какая ось у него. Так свотч палитры показывает
   * её с текущим материалом, и наоборот (превью отражает реальный вид).
   */
  readonly appearanceFor: (itemId: string) => { paletteId: string; ballStyleId: string };
  /** Вызывается при выборе ДРУГОГО свотча (тот же — не дёргает). */
  readonly onSelect: (id: string) => void;
}

export interface SwatchGalleryHandle {
  /** Корневая сетка свотчей (role=list) — вкладка вставляет в свой tabpanel. */
  readonly element: HTMLElement;
  /** Программно сменить выбранный свотч ЭТОЙ оси (без вызова onSelect). */
  setSelected(id: string): void;
  /** Перерисовать ВСЕ превью (когда сменилась ДРУГАЯ ось → комбинации поменялись). */
  refreshPreviews(): void;
  /** Снять board-view'шки + DOM. */
  destroy(): void;
}

interface SwatchEntry {
  readonly id: string;
  readonly card: HTMLElement;
  readonly thumb: HTMLElement;
  /** Перерисовать превью этого свотча (палитра на доску / материал на 7 шаров). */
  readonly repaint: () => void;
  /** Снять ресурсы (board-view в board-режиме; balls-режим — no-op). */
  readonly dispose: () => void;
}

/** Построить сетку свотчей одной оси. */
export function mountSwatchGallery(opts: SwatchGalleryOptions): SwatchGalleryHandle {
  const grid = document.createElement('div');
  grid.className = 'skin-gallery-grid';
  grid.setAttribute('role', 'list');
  grid.setAttribute('aria-label', opts.ariaLabel);

  let selectedId = opts.selectedId;
  const entries: SwatchEntry[] = [];

  const applySelected = (): void => {
    for (const e of entries) {
      const isSel = e.id === selectedId;
      e.card.classList.toggle('skin-card--selected', isSel);
      e.card.setAttribute('aria-pressed', String(isSel));
    }
  };

  for (const item of opts.items) {
    const entry = buildSwatch(item, opts.mode, opts.appearanceFor, () => {
      if (selectedId === item.id) return;
      selectedId = item.id;
      applySelected();
      opts.onSelect(item.id);
    });
    entry.repaint();
    grid.appendChild(entry.card);
    entries.push(entry);
  }

  applySelected();

  return {
    element: grid,
    setSelected(id: string): void {
      if (id === selectedId) return;
      selectedId = id;
      applySelected();
    },
    refreshPreviews(): void {
      for (const e of entries) e.repaint();
    },
    destroy(): void {
      for (const e of entries) e.dispose();
      if (grid.parentNode) grid.parentNode.removeChild(grid);
    },
  };
}

/** Один свотч оси: thumb (доска-без-шаров ИЛИ 7-шаров) + имя + слот бейджа + ✓. */
function buildSwatch(
  item: SwatchItem,
  mode: 'board' | 'balls',
  appearanceFor: (id: string) => { paletteId: string; ballStyleId: string },
  onSelect: () => void,
): SwatchEntry {
  // Все свотчи выбираемы (premium-флаг сейчас без гейта, §2.3) → всегда <button>.
  const card = document.createElement('button');
  card.className = 'skin-card';
  card.type = 'button';
  card.dataset.skinId = item.id;
  card.setAttribute('role', 'listitem');
  card.setAttribute('aria-label', item.name);
  card.addEventListener('click', onSelect);

  const thumb = document.createElement('div');
  thumb.className = 'skin-card-thumb';
  thumb.setAttribute('aria-hidden', 'true');
  card.appendChild(thumb);

  let repaint: () => void;
  let dispose: () => void;

  if (mode === 'board') {
    // Ось «Доска»: мини-доска БЕЗ шаров — только фон/клетки палитры (owner 2026-06-16).
    const board = mountBoardView(thumb, {
      interactive: false,
      ariaLabel: t('game.appearance.previewItemAria', { name: item.name }),
    });
    const empty = startState(PREVIEW_SEED).grid.map((row) => row.map(() => 0 as const));
    board.setScore(0);
    board.setMoves(0);
    board.renderNiche([], false); // ниша без шаров
    board.renderStaticGrid(empty, undefined); // поле без шаров
    repaint = (): void => applyAppearance(board.frame, appearanceFor(item.id));
    dispose = (): void => board.unmount();
  } else {
    // Ось «Шары»: компактный образец — 3 шара (красный/синий/зелёный), БЕЗ доски.
    const sample = mountBallsSample(thumb, [1, 3, 2]);
    repaint = (): void => {
      sample.setAttribute('data-ball-style', appearanceFor(item.id).ballStyleId);
    };
    dispose = (): void => {};
  }

  // Слот бейджа (AC-5.1): рендерим только при непустом badge (сейчас пуст → скрыт),
  // вёрстка сетки не меняется при включении монетизации.
  if (item.badge) {
    const badge = document.createElement('span');
    badge.className = 'skin-card-badge';
    badge.textContent = item.badge;
    thumb.appendChild(badge);
  }

  // ✓ выбранного (один маркер — accent-ободок + галочка, §8 single source marker).
  const check = document.createElement('span');
  check.className = 'skin-card-check';
  check.setAttribute('aria-hidden', 'true');
  check.textContent = '✓';
  thumb.appendChild(check);

  const name = document.createElement('span');
  name.className = 'skin-card-name';
  name.textContent = item.name;
  card.appendChild(name);

  return { id: item.id, card, thumb, repaint, dispose };
}

/** 7 шаров (bc1..bc7) в контейнере с data-ball-style — образец материала БЕЗ доски.
 *  Размер шаров — от ширины контейнера (cqw), без хардкода (см. skin-gallery.css). */
export function mountBallsSample(
  host: HTMLElement,
  colors: readonly number[] = [1, 2, 3, 4, 5, 6, 7],
): HTMLElement {
  const sample = document.createElement('div');
  sample.className = 'balls-sample';
  sample.setAttribute('aria-hidden', 'true');
  for (const c of colors) {
    const ball = document.createElement('div');
    ball.className = `ball static bc${c}`;
    sample.appendChild(ball);
  }
  host.appendChild(sample);
  return sample;
}
